import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
// noinspection ES6PreferShortImport
import { AppActionsBuilder } from '../../utils/app-actions-builder/app-actions-builder';
import {
  ColAction,
  ColActionId,
  IoniconsOutline,
  PtEntity,
} from '@pt/pt-models';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import {
  AiAssessmentsService,
  AppAssessmentsService,
  CovidAssessmentsService,
  DataBreachProcessService,
  GlobalMeasureChecksService,
  TreatmentService,
} from '@pt/api-client';
import { Observable, of, Subject } from 'rxjs';
import { PermissionManager, PtCoreSelectors } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pt-table-col-actions',
  templateUrl: './table-col-actions.component.html',
  styles: [
    `
      .table-action:first-of-type > .table-action_separator {
        display: none;
      }
      .no-operations-available {
        font-size: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        width: 160px;
        height: 50px;
      }
    `,
  ],
})
export class TableColActionsComponent implements AfterViewInit, OnDestroy {
  @Input() actions: ColAction[];

  @Input() entityId: number;

  @Output() emitAction = new EventEmitter<ColAction>();

  @Input() entity: PtEntity;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() node: any; //any entity payload

  @Output() supportedActions = new EventEmitter<ColAction[]>();

  public isEmptyVisibleAction = false;

  public loading = true;
  public IconMenu = IoniconsOutline.menu;
  public newActions: ColAction[];
  private destroy$ = new Subject<void>();
  private isMicrosoftAuth$ = this.store.select(
    PtCoreSelectors.isActiveMicrosoftModule
  );
  private isMicrosoftAuth = false;

  private isExternalUserManagement$ = this.store.select(
    PtCoreSelectors.isExternalUserManagement
  );
  private isExternalUserManagement = false;
  constructor(
    private translateService: TranslateService,
    private treatmentService: TreatmentService,
    private appAssessmentService: AppAssessmentsService,
    private aiAssessmentService: AiAssessmentsService,
    private globalMeasureChecksService: GlobalMeasureChecksService,
    private covidAssessmentsService: CovidAssessmentsService,
    private dataBreachService: DataBreachProcessService,
    private permissionManager: PermissionManager,
    private store: Store
  ) {
    this.isExternalUserManagement$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isExternalUserManagement) => {
        this.isExternalUserManagement = isExternalUserManagement;
      });
    this.isMicrosoftAuth$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMicrosoftAuth) => {
        this.isMicrosoftAuth = isMicrosoftAuth;
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterViewInit() {
    this.loading = false;
  }

  clickMenu() {
    this.loading = true;
    let service: Observable<AvailableOperations> | undefined = undefined;
    switch (this.entity) {
      case 'dataBreach':
        service = this.dataBreachService.getDocUserPermission(this.entityId);
        break;
      case 'aiAssessment':
        service = this.aiAssessmentService.getDocUserPermission(this.entityId);
        break;
      case 'appAssessment':
        service = this.appAssessmentService.getDocUserPermission(this.entityId);
        break;
      case 'covidAssessment':
        service = this.covidAssessmentsService.getDocUserPermission(
          this.entityId
        );
        break;
      case 'globalMeasureCheck':
        service = this.globalMeasureChecksService.getDocUserPermission(
          this.entityId
        );
        break;
      case 'treatmentsHolder':
      case 'treatmentsResponsible':
      case 'treatmentsSubResponsible':
      case 'projectCards':
      case 'dpia':
      case 'lia':
        service = this.treatmentService.getDocUserPermission(this.entityId);
        break;
    }

    if (service && this.entityId) {
      service.subscribe({
        next: (success: AvailableOperations) => {
          if (this.entity === 'dataBreach') {
            this.clickMenuDataBreach(success, this.entityId);
          } else {
            const actionsBuilder = new AppActionsBuilder(
              this.translateService,
              this.permissionManager
            );
            this.newActions = actionsBuilder.ofDetailsPageBPM(
              this.entity,
              success
            );
            this.checkAction();
            this.loading = false;
          }
        },
        error: () => {
          this.newActions = [];
          this.checkAction();
          this.loading = false;
        },
      });
    } else {
      const actionsBuilder = new AppActionsBuilder(
        this.translateService,
        this.permissionManager
      );
      this.newActions = actionsBuilder.ofTable(this.entity, this.node);

      if (this.entity === 'users') {
        if (this.isMicrosoftAuth || this.isExternalUserManagement) {
          this.newActions = this.newActions.filter(
            (a) => a.id !== ColActionId.unlockUserAccess
          );

          if (this.isExternalUserManagement) {
            this.newActions = this.newActions.map((a) => {
              switch (a.id) {
                case ColActionId.edit:
                  return {
                    ...a,
                    label: of('Dettagli'),
                  };
                  // TODO: gestire anche l'externalId appena verrà configurato
                case ColActionId.delete:
                {
                  const active = this.node.active;
                  return {
                    ...a,
                    disabled: active,
                  };
                }

                case ColActionId.showDetail:
                default:
                  return a;
              }
            });
          }
        }
      }

      this.loading = false;
      this.checkAction();
    }
  }

  private checkAction() {
    this.isEmptyVisibleAction =
      this.newActions.filter((f) => f.visible).length === 0;
    if (this.isEmptyVisibleAction) {
      this.supportedActions.emit([]);
    } else {
      this.supportedActions.emit(this.newActions.filter((f) => f.visible));
    }
  }

  private clickMenuDataBreach(
    availableOperations: AvailableOperations,
    dataBreachId: number
  ) {
    this.dataBreachService.getDetails(dataBreachId).subscribe({
      next: () => {
        const actionsBuilder = new AppActionsBuilder(
          this.translateService,
          this.permissionManager
        );
        this.newActions = actionsBuilder.ofDetailsPageDataBreach2(
          this.entity,
          availableOperations
        );
        this.checkAction();
        this.loading = false;
      },
      error: () => {
        this.newActions = [];
        this.checkAction();
        this.loading = false;
      },
    });
  }
}
